// dev-garage-modal — site-wide "My Garage" popup (opened from the header button).
// ───────────────────────────────────────────────────────────────────────────
// Standalone counterpart to the PDP block's garage manager. It has no product to
// fit against, so it simply lists the saved garage (localStorage['devGarage'] —
// the key shared with blocks/dev-shop-my-ride.js and sections/dev-garage.js):
//   • empty  → benefits + "Add Your Ride"            (Figma 591-15745)
//   • filled → + one row per ride, Shop Ride / Clear (Figma 787-24647)
//
// Markup: snippets/dev-garage-global.liquid. Styling reuses .dev-smr__* from
// assets/dev-shop-my-ride.css; silhouettes come from snippets/dev-vehicle-icons.
//
// Opening: a click on any [data-dev-garage-open] element (the header button), or a
// `dev-garage:open` event dispatched on document.

(function () {
  'use strict';

  const NAME = 'dev-garage-modal';
  if (customElements.get(NAME)) return;

  const GARAGE_KEY = 'devGarage';
  const q = (root, sel) => root.querySelector(sel);
  const escapeHtml = (s) =>
    String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  // vehicle-type key → matching <template data-smr-veh="…"> (see dev-vehicle-icons).
  function vehicleType(parts) {
    const type = (parts && parts[0] && String(parts[0].value).toLowerCase()) || '';
    if (type.includes('snow')) return 'snowmobile';
    // No dedicated Side-by-Side silhouette exists yet — reuse the ATV one
    // (both are 4-wheel/off-road), rather than falling through to the
    // dirt-bike-looking default below. Kept in sync with the duplicate copy
    // in dev-shop-my-ride.js (see that file's own comment on this function).
    if (type.includes('atv') || type.includes('quad') || type.includes('side')) return 'atv';
    if (type.includes('motorcycle') || type.includes('street') || type.includes('dual')) return 'moto';
    return 'atv';
  }

  customElements.define(
    NAME,
    class extends HTMLElement {
      #initialized = false;
      #vehTpls = {};
      #vehicles = [];
      #onKeydown = null;
      #onOpen = null;
      #onDocClick = null;

      connectedCallback() {
        if (this.#initialized) return;
        this.#initialized = true;

        this.shopUrl = this.dataset.shopUrl || '/collections/all';
        this.addUrl = this.dataset.addUrl || '/';
        this.modalEl = q(this, '[data-dgm-modal]');
        this.rowsEl = q(this, '[data-dgm-rows]');
        this.rowTpl = q(this, '[data-dgm-row-tpl]');
        if (!this.modalEl || !this.rowsEl || !this.rowTpl) return;

        this.querySelectorAll('[data-smr-veh]').forEach((t) => {
          this.#vehTpls[t.dataset.smrVeh] = t.innerHTML;
        });

        this.#bind();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.#onKeydown);
        document.removeEventListener('dev-garage:open', this.#onOpen);
        document.removeEventListener('click', this.#onDocClick);
      }

      #bind() {
        // Close: X / scrim / Esc
        this.querySelectorAll('[data-dgm-close]').forEach((el) => el.addEventListener('click', () => this.close()));
        this.#onKeydown = (e) => {
          if (e.key === 'Escape' && !this.modalEl.hidden) this.close();
        };
        document.addEventListener('keydown', this.#onKeydown);

        // "Add Your Ride" → the homepage "Your Garage" finder (addUrl ends in #your-garage).
        // Close first so body scroll is restored — otherwise, when already on the homepage,
        // the hash-only change won't reload and the open modal would block the scroll.
        const addBtn = q(this, '[data-dgm-add]');
        if (addBtn) addBtn.addEventListener('click', () => { this.close(); window.location.href = this.addUrl; });

        // Open: header button(s) or a programmatic event. Delegated so the trigger
        // can live anywhere on the page without knowing about this element.
        this.#onDocClick = (e) => {
          const trigger = e.target.closest && e.target.closest('[data-dev-garage-open]');
          if (trigger) { e.preventDefault(); this.open(); }
        };
        document.addEventListener('click', this.#onDocClick);
        this.#onOpen = () => this.open();
        document.addEventListener('dev-garage:open', this.#onOpen);
      }

      // ── garage (localStorage) ────────────────────────────────────────────
      #readGarage() {
        try {
          const v = JSON.parse(localStorage.getItem(GARAGE_KEY));
          return Array.isArray(v) ? v : [];
        } catch {
          return [];
        }
      }
      #writeGarage(list) {
        try { localStorage.setItem(GARAGE_KEY, JSON.stringify(list)); } catch { /* quota / private mode */ }
        document.dispatchEvent(new CustomEvent('dev-garage:change')); // notify cart badges etc.
      }

      #iconHtml(parts) {
        return this.#vehTpls[vehicleType(parts)] || this.#vehTpls.bike || '';
      }

      // PLP filtered by a ride's fitment (filter.fitments=type>year>make>model).
      #shopUrlFor(parts) {
        const vals = (parts || []).map((p) => p.value);
        const base = this.shopUrl || '/collections/all';
        return `${base}?filter.fitments=${encodeURIComponent(vals.join('>'))}`;
      }

      // ── open / render / close ────────────────────────────────────────────
      open() {
        this.#vehicles = this.#readGarage();
        this.#renderRows();
        this.modalEl.hidden = false;
        document.body.style.overflow = 'hidden';
      }

      close() {
        this.modalEl.hidden = true;
        document.body.style.overflow = '';
      }

      #renderRows() {
        this.rowsEl.innerHTML = '';
        // No rides → hide the list so the empty state keeps a single 20px rhythm.
        this.rowsEl.hidden = this.#vehicles.length === 0;
        this.#vehicles.forEach((veh, i) => {
          const row = this.rowTpl.content.firstElementChild.cloneNode(true);
          q(row, '[data-dgm-icon]').innerHTML = this.#iconHtml(veh.parts);
          q(row, '[data-dgm-name]').innerHTML =
            veh.parts && veh.parts.length
              ? veh.parts.map((p) => `<span>${escapeHtml(p.value)}</span>`).join('')
              : escapeHtml(veh.name);
          q(row, '[data-dgm-shop]').href = this.#shopUrlFor(veh.parts);
          q(row, '[data-dgm-clear]').addEventListener('click', () => this.#clearRide(i));
          this.rowsEl.appendChild(row);
        });
      }

      #clearRide(i) {
        const veh = this.#vehicles[i];
        if (!veh) return;
        this.#writeGarage(this.#readGarage().filter((x) => x && x.name !== veh.name));
        this.#vehicles.splice(i, 1);
        this.#renderRows();
      }
    },
  );
})();
